import { useState, type FormEvent } from 'react';
import { api } from '../api';
import type { Tarif } from '../billtoro/types';
import { fehlendeFunktion, tarifFehlerFuer, upgradeAnbieten } from '../billtoro/upgrade';
import { eur } from '../lib/money';
import { useToast } from '../lib/toast';
import { ChipRadios } from './ChipRadios';
import { bodyAus, formAus, mahnlaufUrl, type EinstellungForm, type MahnlaufAntwort, type MahnlaufEinstellung as Einstellung, type Modus, type Stufe, type StufeForm } from './MahnlaufDaten';
import { darf, fehlerText, GesperrtKnopf, NurLesen, OHNE_RECHT_EINSTELLUNGEN } from './tarifHinweis';
import { MAHN_TITLES } from './types';
import './einstellungen.css';

const MODI: [Modus, string][] = [
  ['vorschlagen', 'Nur vorschlagen'],
  ['senden', 'Automatisch senden'],
];
const MODUS_TEXT: Record<Modus, string> = {
  vorschlagen: 'Jeden Morgen eine Liste. Sie senden mit einem Klick.',
  senden: 'Fällige Mahnungen gehen jeden Tag ab 9 Uhr per E-Mail raus.',
};

// Einstellung der Mahnläufe (Karte auf der Seite „Mahnläufe“): an/aus, nur vorschlagen oder automatisch senden, je
// Stufe wann und mit welcher Frist, Gebühren ab der Mahnung, bis zu welcher Stufe, ab welchem offenen Betrag, E-Mail
// an den Inhaber. Prüfen und speichern macht der Server; ändern nur mit dem Recht „Einstellungen“, einschalten und bei
// eingeschaltetem Lauf ändern zusätzlich mit „Rechnungen“ (Billtoro). Billtoro ohne die Mahnläufe im Tarif: grau,
// „Einschalten“ öffnet „Mehr mit Billtoro“ (ausschalten geht immer).
export function MahnlaufEinstellung({
  firmId,
  einstellung,
  tarif,
  onSaved,
}: {
  firmId: string;
  einstellung: Einstellung;
  tarif?: Tarif | null;
  onSaved(m: MahnlaufAntwort): Promise<void> | void;
}) {
  const toast = useToast();
  const lesen = !darf(tarif, 'einstellungen');
  const ohneRechnungen = !lesen && !darf(tarif, 'rechnungen');
  const fehlt = fehlendeFunktion(tarif, 'mahnlauf');
  const [f, setF] = useState<EinstellungForm>(() => formAus(einstellung));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const setStufe = (i: number, s: StufeForm) => setF({ ...f, stufen: f.stufen.map((x, j) => (j === i ? s : x)) as EinstellungForm['stufen'] });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (lesen) return;
    setError(null);
    setBusy(true);
    try {
      const m = await api.put<MahnlaufAntwort>(mahnlaufUrl(firmId), bodyAus(f));
      setF(formAus(m.einstellung));
      await onSaved(m);
      toast('Gespeichert');
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="rt-card rt-set ml-set" onSubmit={submit} noValidate aria-labelledby="ml-set-h">
      <h2 id="ml-set-h">Einstellungen</h2>
      {fehlt && tarif && <p className="rt-hint">{tarifFehlerFuer(tarif, 'mahnlauf')?.error}</p>}
      {ohneRechnungen && <p className="rt-hint">Einschalten und ändern, solange er läuft, nur mit dem Recht „Rechnungen“. Ausschalten geht.</p>}
      <NurLesen an={lesen} hinweis={OHNE_RECHT_EINSTELLUNGEN}>
        {fehlt && !f.an ? (
          <GesperrtKnopf tarif={tarif} fehlt={fehlt} className="btn ml-ein" onUpgrade={() => upgradeAnbieten(tarif, 'mahnlauf')}>
            Mahnläufe einschalten
          </GesperrtKnopf>
        ) : (
          <label className="check">
            <input type="checkbox" checked={f.an} onChange={(e) => setF({ ...f, an: e.target.checked })} />
            <span>Mahnläufe einschalten</span>
          </label>
        )}
        <fieldset className="ml-felder" disabled={!!fehlt}>
          <p className="ml-frage">Was soll passieren?</p>
          <ChipRadios label="Was soll passieren?" value={f.modus} options={MODI} onChange={(modus) => setF({ ...f, modus })} />
          <p className="rt-hint ml-modus">{MODUS_TEXT[f.modus]}</p>
          {f.stufen.map((s, i) => (
            <StufeFelder key={i} nr={i} s={s} onChange={(v) => setStufe(i, v)} />
          ))}
          <div className="rt-grid ml-grid">
            <label className="field">
              <span>Automatisch bis</span>
              <select value={f.bisStufe} onChange={(e) => setF({ ...f, bisStufe: Number(e.target.value) as Stufe })}>
                {MAHN_TITLES.map((t, i) => (
                  <option key={t} value={i + 1}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Nur ab offenem Betrag von</span>
              <span className="ml-unit">
                <input value={f.abBetrag} inputMode="decimal" aria-label="Nur ab offenem Betrag von (Euro)" onChange={(e) => setF({ ...f, abBetrag: e.target.value })} />
                <span>€</span>
              </span>
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={f.bericht} onChange={(e) => setF({ ...f, bericht: e.target.checked })} />
            <span>Nach jedem Lauf eine E-Mail {tarif?.rolle === 'mitglied' ? 'an den Inhaber' : 'an mich'}</span>
          </label>
        </fieldset>
      </NurLesen>
      <p className="rt-hint">Rechnungen ohne E-Mail-Adresse und mit „Nicht automatisch mahnen“ lässt der Mahnlauf aus.</p>
      <p className="rt-hint">Die E-Mails haben dieselben Texte wie beim Verschicken von Hand.</p>
      {error && <div className="notice bad">{error}</div>}
      {!lesen && (
        <div className="rt-actions">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </div>
      )}
    </form>
  );
}

// Eine Stufe: wann (Zahlungserinnerung nach der Fälligkeit, danach nach der vorigen Frist), Frist, Gebühren (nicht bei
// der Zahlungserinnerung)
function StufeFelder({ nr, s, onChange }: { nr: number; s: StufeForm; onChange(s: StufeForm): void }) {
  const titel = MAHN_TITLES[nr]!;
  const set = (v: Partial<StufeForm>) => onChange({ ...s, ...v });
  return (
    <fieldset className="rt-kreis ml-stufe">
      <legend>{titel}</legend>
      <div className="rt-grid ml-grid">
        <label className="field">
          <span>{nr === 0 ? 'Tage nach Fälligkeit' : 'Tage nach Ablauf der vorigen Frist'}</span>
          <input value={s.tage} inputMode="numeric" aria-label={`${titel}: ${nr === 0 ? 'Tage nach Fälligkeit' : 'Tage nach Ablauf der vorigen Frist'}`} onChange={(e) => set({ tage: e.target.value })} />
        </label>
        <label className="field">
          <span>Frist in Tagen</span>
          <input value={s.frist} inputMode="numeric" aria-label={`${titel}: Frist in Tagen`} onChange={(e) => set({ frist: e.target.value })} />
        </label>
      </div>
      {nr === 0 ? (
        <p className="rt-hint ml-ohne">Ohne Gebühr</p>
      ) : (
        <div className="ml-gebuehren">
          <div>
            <p className="ml-wer">Firmen und Behörden</p>
            <label className="check">
              <input type="checkbox" checked={s.pauschale} aria-label={`${titel}: Pauschale für Firmen und Behörden`} onChange={(e) => set({ pauschale: e.target.checked })} />
              <span>{eur(40)} Pauschale (einmal)</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={s.zinsenFirma} aria-label={`${titel}: Zinsen für Firmen und Behörden`} onChange={(e) => set({ zinsenFirma: e.target.checked })} />
              <span>Zinsen</span>
            </label>
          </div>
          <div>
            <p className="ml-wer">Privatkunden</p>
            <label className="field ml-geb">
              <span>Gebühr (höchstens {eur(5)})</span>
              <span className="ml-unit">
                <input value={s.gebuehr} inputMode="decimal" aria-label={`${titel}: Gebühr für Privatkunden (Euro)`} onChange={(e) => set({ gebuehr: e.target.value })} />
                <span>€</span>
              </span>
            </label>
            <label className="check">
              <input type="checkbox" checked={s.zinsenPrivat} aria-label={`${titel}: Zinsen für Privatkunden`} onChange={(e) => set({ zinsenPrivat: e.target.checked })} />
              <span>Zinsen</span>
            </label>
          </div>
        </div>
      )}
    </fieldset>
  );
}
